import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  APPROVAL_REQUESTS,
  DECISION_CONFIG,
  STAGE_BADGE,
  approvalRoute,
  detailPath,
  isDecided,
  nextStage,
} from "./approvalData";
import "./approvals.css";

/* Approval Queue — ported from the prototype (approval.html).
   Category tabs with counts, one-row filters, requestor avatars, the 3-step
   approval flow per row, and Approve / Return / Reject decisions with a
   remarks modal. Approving advances the row's stage in place. */

const TABS = [
  { key: "all", label: "All" },
  { key: "travel", label: "Travel Request" },
  { key: "training", label: "Training Request" },
  { key: "claim", label: "Claims" },
];

const ApprovalQueuePage = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState(() => APPROVAL_REQUESTS.map((request) => ({ ...request })));
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("All Dates");
  const [decision, setDecision] = useState(null); // { id, type }
  const [remarks, setRemarks] = useState("");
  const [remarksError, setRemarksError] = useState(false);
  const [exported, setExported] = useState(false);

  const counts = useMemo(() => {
    const byCategory = { all: requests.length };
    requests.forEach((request) => {
      byCategory[request.category] = (byCategory[request.category] || 0) + 1;
    });
    return byCategory;
  }, [requests]);

  const pendingCount = useMemo(() => requests.filter((request) => !isDecided(request.stage)).length, [requests]);

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return requests.filter((request) => {
      if (activeTab !== "all" && request.category !== activeTab) return false;
      if (stageFilter !== "all" && request.stage !== stageFilter) return false;
      if (!query) return true;
      return [request.id, request.typeLabel, request.subtitle, request.requestor.name, request.requestor.role]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [requests, activeTab, search, stageFilter]);

  const resetFilters = () => {
    setSearch("");
    setStageFilter("all");
    setDateFilter("All Dates");
    setActiveTab("all");
  };

  const openDecision = (id, type) => {
    setDecision({ id, type });
    setRemarks("");
    setRemarksError(false);
  };

  const closeDecision = () => {
    setDecision(null);
    setRemarks("");
    setRemarksError(false);
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeDecision();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const confirmDecision = () => {
    if (!decision) return;
    const config = DECISION_CONFIG[decision.type];
    if (config.remarksRequired && !remarks.trim()) {
      setRemarksError(true);
      return;
    }
    setRequests((prev) =>
      prev.map((request) => {
        if (request.id !== decision.id) return request;
        if (decision.type === "approve") return { ...request, stage: nextStage(request) };
        if (decision.type === "return") return { ...request, stage: "returned" };
        return { ...request, stage: "rejected" };
      })
    );
    closeDecision();
  };

  const exportApprovals = () => {
    const header = "Request ID,Type,Requestor,Submitted,Amount (RM),Current Stage";
    const lines = rows.map((request) =>
      [request.id, request.typeLabel, request.requestor.name, request.submitted, request.amount, STAGE_BADGE[request.stage].label]
        .map((value) => `"${value}"`)
        .join(",")
    );
    const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "approval-queue.csv";
    link.click();
    URL.revokeObjectURL(url);
    setExported(true);
    window.setTimeout(() => setExported(false), 2500);
  };

  const decisionRequest = decision ? requests.find((request) => request.id === decision.id) : null;
  const decisionConfig = decision ? DECISION_CONFIG[decision.type] : null;

  const renderFlow = (request) => {
    if (isDecided(request.stage)) return null;
    const { labels, stages } = approvalRoute(request);
    const currentIndex = Math.max(stages.indexOf(request.stage), 0);
    return (
      <div className="approval-flow">
        {labels.map((label, index) => {
          const state = index < currentIndex ? "done" : index === currentIndex ? "current" : "";
          return (
            <React.Fragment key={label}>
              {index > 0 && <span className="flow-line" />}
              <span className={`flow-step ${state}`}>
                <span className="flow-dot">{state === "done" ? <i className="ri-check-line" /> : index + 1}</span> {label}
              </span>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bes-approvals">
      <div className="approvals-page-head">
        <div className="title-left">
          <div className="page-icon">
            <i className="ri-checkbox-circle-line" />
          </div>
          <div>
            <h1>Approval Queue</h1>
            <p>Review and process Travel Requests, Training Requests and Claims according to the approval hierarchy.</p>
          </div>
        </div>
        <div className="head-actions">
          {exported && <span className="exported-note">Exported.</span>}
          <button type="button" className="btn btn-outline" onClick={exportApprovals}>
            <i className="ri-download-line" /> Export
          </button>
        </div>
      </div>

      <div className="approval-summary">
        <div className="summary-card">
          <div className="summary-icon icon-amber">
            <i className="ri-time-line" />
          </div>
          <div>
            <h3>Pending My Approval</h3>
            <strong>{pendingCount}</strong>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon icon-blue">
            <i className="ri-flight-takeoff-line" />
          </div>
          <div>
            <h3>Travel Requests</h3>
            <strong>{counts.travel || 0}</strong>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon icon-purple">
            <i className="ri-graduation-cap-line" />
          </div>
          <div>
            <h3>Training Requests</h3>
            <strong>{counts.training || 0}</strong>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon icon-green">
            <i className="ri-file-list-2-line" />
          </div>
          <div>
            <h3>Claims</h3>
            <strong>{counts.claim || 0}</strong>
          </div>
        </div>
      </div>

      <div className="list-container">
        <div className="list-container-inner">
          <div className="approval-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`approval-tab ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label} <span className="count">{counts[tab.key] || 0}</span>
              </button>
            ))}
          </div>

          <div className="filters-standard">
            <div className="filter-group search">
              <label>Search</label>
              <i className="ri-search-line" />
              <input
                className="form-control"
                type="text"
                placeholder="Search request ID, employee, destination or purpose..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Approval Stage</label>
              <select className="form-control" value={stageFilter} onChange={(event) => setStageFilter(event.target.value)}>
                <option value="all">All Stages</option>
                <option value="hou">Head of Unit</option>
                <option value="hod">Head of Department</option>
                <option value="ceo">CEO</option>
                <option value="admin">Admin</option>
                <option value="finance">Finance</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Submitted Date</label>
              <select className="form-control" value={dateFilter} onChange={(event) => setDateFilter(event.target.value)}>
                <option>All Dates</option>
                <option>Today</option>
                <option>Last 7 Days</option>
                <option>Last 30 Days</option>
              </select>
            </div>
            <button type="button" className="btn btn-outline" onClick={resetFilters}>
              <i className="ri-refresh-line" /> Reset
            </button>
          </div>
        </div>

        {rows.length > 0 ? (
          <div className="table-responsive">
            <table className="approval-table">
              <thead>
                <tr>
                  <th>Request</th>
                  <th>Requestor</th>
                  <th>Submitted</th>
                  <th>Amount (RM)</th>
                  <th>Approval Progress</th>
                  <th>Current Stage</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((request) => {
                  const badge = STAGE_BADGE[request.stage];
                  const decided = isDecided(request.stage);
                  return (
                    <tr key={request.id}>
                      <td>
                        <div className="request-title">
                          {request.id} · {request.typeLabel}
                        </div>
                        <div className="request-subtitle">
                          {request.subtitle.split("\n").map((line) => (
                            <div key={line}>{line}</div>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div className="requestor">
                          <div
                            className="requestor-avatar"
                            style={{ background: request.requestor.bg, color: request.requestor.color }}
                          >
                            {request.requestor.initials}
                          </div>
                          <div>
                            <div className="requestor-name">{request.requestor.name}</div>
                            <div className="requestor-role">{request.requestor.role}</div>
                          </div>
                        </div>
                      </td>
                      <td>{request.submitted}</td>
                      <td className="font-bold">{request.amount}</td>
                      <td>{renderFlow(request) || <span className="text-muted flow-ended">Workflow ended</span>}</td>
                      <td>
                        <span className={`status-badge ${badge.cls}`}>{badge.label}</span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            type="button"
                            className="action-btn"
                            title="View"
                            onClick={() => navigate(detailPath(request.category, request.id))}
                          >
                            <i className="ri-eye-line" />
                          </button>
                          {!decided && (
                            <>
                              <button
                                type="button"
                                className="action-btn approve"
                                title="Approve"
                                onClick={() => openDecision(request.id, "approve")}
                              >
                                <i className="ri-check-line" />
                              </button>
                              <button
                                type="button"
                                className="action-btn return"
                                title="Return for Revision"
                                onClick={() => openDecision(request.id, "return")}
                              >
                                <i className="ri-arrow-go-back-line" />
                              </button>
                              <button
                                type="button"
                                className="action-btn reject"
                                title="Reject"
                                onClick={() => openDecision(request.id, "reject")}
                              >
                                <i className="ri-close-line" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <i className="ri-folder-open-line" />
            <div className="empty-title">No approval requests found</div>
            <div>Try changing the selected category or filter.</div>
          </div>
        )}

        <div className="table-footer">
          <span>{rows.length === 0 ? "No requests found" : `Showing 1 to ${rows.length} of ${rows.length} requests`}</span>
          <div className="pagination">
            <button type="button" className="page-btn">
              <i className="ri-arrow-left-s-line" />
            </button>
            <button type="button" className="page-btn active">
              1
            </button>
            <button type="button" className="page-btn">
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        </div>
      </div>

      {decision && decisionRequest && (
        <div
          className="decision-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="decisionModalTitle"
          onClick={(event) => {
            if (event.target === event.currentTarget) closeDecision();
          }}
        >
          <div className="decision-modal-box">
            <div className="decision-modal-header">
              <h3 id="decisionModalTitle">{decisionConfig.title}</h3>
              <p>
                {decisionRequest.id} · {decisionRequest.typeLabel}
              </p>
            </div>
            <div className="decision-modal-body">
              <label>
                Remarks{decisionConfig.remarksRequired ? <span className="required"> *</span> : null}
              </label>
              <textarea
                className={`form-control ${remarksError ? "has-error" : ""}`}
                placeholder="Enter remarks or comments..."
                value={remarks}
                onChange={(event) => {
                  setRemarks(event.target.value);
                  if (remarksError && event.target.value.trim()) setRemarksError(false);
                }}
              />
              {remarksError && <div className="remarks-error">Please enter remarks before continuing.</div>}
              <div className={`decision-notice ${decisionConfig.noticeCls}`}>{decisionConfig.notice}</div>
            </div>
            <div className="decision-modal-actions">
              <button type="button" className="btn btn-outline" onClick={closeDecision}>
                Cancel
              </button>
              <button type="button" className={`btn ${decisionConfig.confirmCls}`} onClick={confirmDecision}>
                {decisionConfig.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalQueuePage;
